import { isIOS } from './deviceDetection';

let audioContext: globalThis.AudioContext | null = null;
const gainNodes: Map<number, globalThis.GainNode> = new Map();
const mediaElementSources: Map<number, globalThis.MediaElementAudioSourceNode> = new Map();
// Track file ID to player number mapping for reliable remapping
const fileIdToPlayerNumber: Map<string, number> = new Map();
const mobileFilterNodes: Map<number, globalThis.BiquadFilterNode[]> = new Map();
const mobileInputGainNodes: Map<number, globalThis.GainNode> = new Map();
const mobileStereoChains: Map<number, {
  splitter: ChannelSplitterNode;
  leftGain: GainNode;
  rightGain: GainNode;
  merger: ChannelMergerNode;
}> = new Map();
let isContextInitialized = false;
let resumeListenersAdded = false;

// Stereo processing modes
export type StereoMode = 'stereo' | 'mono' | 'side';

export type FilterType = 'off' | 'highpass' | 'lowpass' | 'bandpass';
export type FilterSlope = 12 | 24; // dB/octave (only achievable slopes with BiquadFilterNode)

// Filter state (used by per-context processing)
let currentFilterType: FilterType = 'off';
let currentFilterFrequency: number = 1000;
let currentFilterFrequencyLow: number = 500; // For BP filter
let currentFilterFrequencyHigh: number = 2000; // For BP filter
let currentFilterQ: number = 0.707;
let currentFilterSlope: FilterSlope = 12; // dB/octave for HP/LP
let currentFilterInputGain: number = 1.0; // Input gain before filters (default unity)

// Track previous filter config to determine if we can reuse nodes
let previousFilterType: FilterType = 'off';
let previousFilterSlope: FilterSlope = 12;

// Stereo mode state (used by mobile processing)
let currentStereoMode: StereoMode = 'stereo';

// Dim state (master volume dimming)
let isDimmed = false;
const DIM_DB = -15; // -15dB dimming
const DIM_LINEAR_GAIN = Math.pow(10, DIM_DB / 20); // Convert dB to linear gain (~0.1778)
const DIM_FADE_TIME = 1.0; // 1 second fade time
let masterDimGainNode: GainNode | null = null; // For mobile audio context
const desktopDimGainNodes = new Map<AudioContext, GainNode>(); // For desktop (WaveSurfer) audio contexts

const DEBUG = false;

const debugLog = (message: string, data?: Record<string, unknown>) => {
  if (DEBUG) {
    console.log(`[MobileAudio] ${message}`, data || '');
  }
};

// Helper function to ensure audio context is resumed
export const ensureAudioContextResumed = async (): Promise<void> => {
  if (audioContext && audioContext.state === 'suspended') {
    try {
      await audioContext.resume();
      debugLog('WebAudio context resumed');
    } catch (error) {
      console.warn('Failed to resume WebAudio context:', error);
    }
  }
};

export const initializeMobileAudioContext = async (): Promise<globalThis.AudioContext> => {
  if (audioContext && audioContext.state !== 'closed') {
    return audioContext;
  }

  if (!isContextInitialized) {
    debugLog('Initializing WebAudio context');
    try {
      // Create the context but don't start it yet
      const AudioContextClass = window.AudioContext || (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext;
      audioContext = new AudioContextClass();
      isContextInitialized = true;
      
      // Add global interaction handlers to resume the context (not once-only)
      if (!resumeListenersAdded) {
        const resumeContext = async () => {
          await ensureAudioContextResumed();
        };

        // Add persistent event listeners for iOS
        document.addEventListener('click', resumeContext);
        document.addEventListener('touchstart', resumeContext);
        document.addEventListener('touchend', resumeContext);
        
        resumeListenersAdded = true;
        debugLog('Added persistent context resume listeners');
      }
      
      return audioContext;
    } catch (error) {
      console.error('Failed to initialize WebAudio context:', error);
      throw error;
    }
  }

  return audioContext!;
};

export const createMobileGainNode = async (playerNumber: number): Promise<globalThis.GainNode> => {
  const context = await initializeMobileAudioContext();
  
  if (!gainNodes.has(playerNumber)) {
    debugLog(`Creating gain node for player ${playerNumber}`);
    const gainNode = context.createGain();
    gainNode.gain.value = 1; // Start with full volume
    gainNodes.set(playerNumber, gainNode);
  }
  
  return gainNodes.get(playerNumber)!;
};

export const connectMobileMediaElement = async (playerNumber: number, mediaElement: HTMLMediaElement, fileId?: string): Promise<void> => {
  try {
    debugLog(`Connecting media element for player ${playerNumber}`, {
      readyState: mediaElement.readyState,
      paused: mediaElement.paused,
      muted: mediaElement.muted,
      volume: mediaElement.volume
    });

    // Ensure audio element is loaded and ready
    if (mediaElement.readyState < 2) {
      debugLog(`Waiting for audio element ${playerNumber} to load`);
      
      // CRITICAL: Store currentTime before calling load() which resets it to 0
      const originalCurrentTime = mediaElement.currentTime;
      debugLog(`Preserving currentTime ${originalCurrentTime}s before loading player ${playerNumber}`);
      
      try {
        await new Promise<void>((resolve, reject) => {
          const loadHandler = () => {
            mediaElement.removeEventListener('canplaythrough', loadHandler);
            // CRITICAL: Restore currentTime after loading completes
            if (originalCurrentTime > 0) {
              mediaElement.currentTime = originalCurrentTime;
              debugLog(`Restored currentTime to ${originalCurrentTime}s for player ${playerNumber}`);
            }
            resolve();
          };
          const errorHandler = (e: Event) => {
            mediaElement.removeEventListener('error', errorHandler);
            reject(new Error('Failed to load audio element'));
          };
          mediaElement.addEventListener('canplaythrough', loadHandler);
          mediaElement.addEventListener('error', errorHandler);
          
          // For iOS, we need to trigger loading explicitly
          if (isIOS()) {
            try {
              mediaElement.load(); // This resets currentTime to 0, but we restore it in loadHandler
            } catch (error) {
              console.warn(`Failed to load audio element for player ${playerNumber}:`, error);
              // Don't reject here, let the event handlers handle it
            }
          }
        });
      } catch (error) {
        console.warn(`Failed to load audio element for player ${playerNumber}:`, error);
        // For iOS, try one more time
        if (isIOS()) {
          debugLog(`Retrying audio element load for player ${playerNumber}`);
          try {
            await new Promise<void>((resolve, reject) => {
              const loadHandler = () => {
                mediaElement.removeEventListener('canplaythrough', loadHandler);
                // CRITICAL: Restore currentTime after retry loading completes
                if (originalCurrentTime > 0) {
                  mediaElement.currentTime = originalCurrentTime;
                  debugLog(`Restored currentTime to ${originalCurrentTime}s for player ${playerNumber} after retry`);
                }
                resolve();
              };
              const errorHandler = (e: Event) => {
                mediaElement.removeEventListener('error', errorHandler);
                reject(new Error('Failed to load audio element on retry'));
              };
              mediaElement.addEventListener('canplaythrough', loadHandler);
              mediaElement.addEventListener('error', errorHandler);
              mediaElement.load(); // This resets currentTime to 0, but we restore it in loadHandler
            });
          } catch (retryError) {
            console.error(`Failed to load audio element for player ${playerNumber} after retry:`, retryError);
            throw retryError;
          }
        } else {
          throw error;
        }
      }
    }

    // Initialize audio context if not already initialized
    const context = await initializeMobileAudioContext();
    
    // Ensure context is resumed before connecting
    await ensureAudioContextResumed();
    
    // Get or create gain node
    const gainNode = await createMobileGainNode(playerNumber);
    
    // Check if source already exists for this player number
    const existingSource = mediaElementSources.get(playerNumber);
    if (existingSource) {
      debugLog(`Using existing source for player ${playerNumber}`);
      // Update file ID mapping if provided
      if (fileId) {
        fileIdToPlayerNumber.set(fileId, playerNumber);
      }
      // Reconnect with current filter settings
      await reconnectMobileAudioGraph(playerNumber);
      return;
    }
    
    // Check if this file ID already has a source connected at a different player number (from remapping)
    // This can happen if remapping hasn't completed yet or if there's a race condition
    if (fileId) {
      const existingPlayerNumber = fileIdToPlayerNumber.get(fileId);
      if (existingPlayerNumber !== undefined && existingPlayerNumber !== playerNumber) {
        const existingSourceForFile = mediaElementSources.get(existingPlayerNumber);
        if (existingSourceForFile) {
          debugLog(`File ID ${fileId} already has source at player ${existingPlayerNumber}, using it for player ${playerNumber}`);
          // Move the source and all related nodes to the new player number
          mediaElementSources.set(playerNumber, existingSourceForFile);
          mediaElementSources.delete(existingPlayerNumber);
          
          // Move gain node
          const existingGainNode = gainNodes.get(existingPlayerNumber);
          if (existingGainNode) {
            gainNodes.set(playerNumber, existingGainNode);
            gainNodes.delete(existingPlayerNumber);
          }
          
          // Move filter nodes
          const existingFilterNodes = mobileFilterNodes.get(existingPlayerNumber);
          if (existingFilterNodes) {
            mobileFilterNodes.set(playerNumber, existingFilterNodes);
            mobileFilterNodes.delete(existingPlayerNumber);
          }
          
          // Move input gain
          const existingInputGain = mobileInputGainNodes.get(existingPlayerNumber);
          if (existingInputGain) {
            mobileInputGainNodes.set(playerNumber, existingInputGain);
            mobileInputGainNodes.delete(existingPlayerNumber);
          }
          
          // Move stereo chain
          const existingStereoChain = mobileStereoChains.get(existingPlayerNumber);
          if (existingStereoChain) {
            mobileStereoChains.set(playerNumber, existingStereoChain);
            mobileStereoChains.delete(existingPlayerNumber);
          }
          
          // Update file ID mapping
          fileIdToPlayerNumber.set(fileId, playerNumber);
          
          // Reconnect with current filter settings
          await reconnectMobileAudioGraph(playerNumber);
          return;
        }
      }
    }
    
    // Create new source and connect it
    debugLog(`Creating new source for player ${playerNumber}`);
    try {
      const source = context.createMediaElementSource(mediaElement);
      
      // Ensure the audio element is not muted and has correct volume
      mediaElement.muted = false;
      mediaElement.volume = 1;
      
      // Store the source
      mediaElementSources.set(playerNumber, source);
      
      // Track file ID mapping if provided
      if (fileId) {
        // Remove any existing mapping for this file ID (in case of remapping)
        const oldPlayerNumber = fileIdToPlayerNumber.get(fileId);
        if (oldPlayerNumber && oldPlayerNumber !== playerNumber) {
          debugLog(`File ID ${fileId} was mapped to player ${oldPlayerNumber}, remapping to player ${playerNumber}`);
        }
        fileIdToPlayerNumber.set(fileId, playerNumber);
      }
      
      // Connect the audio graph with filters
      await reconnectMobileAudioGraph(playerNumber);
      
      debugLog(`Audio graph connected for player ${playerNumber}`, {
        source: !!source,
        gainNode: !!gainNode,
        contextState: context.state,
        mediaElementState: {
          readyState: mediaElement.readyState,
          paused: mediaElement.paused,
          muted: mediaElement.muted,
          volume: mediaElement.volume
        }
      });
    } catch (error) {
      console.error(`Failed to create media element source for player ${playerNumber}:`, error);
      throw error;
    }
  } catch (error) {
    console.error(`Failed to connect media element for player ${playerNumber}:`, error);
    throw error;
  }
};

export const getMobileGainNode = (playerNumber: number): globalThis.GainNode | null => {
  return gainNodes.get(playerNumber) || null;
};

export const fadeMobileGain = async (
  playerNumber: number,
  targetGain: number,
  duration: number = 20
): Promise<void> => {
  try {
    debugLog(`Fading gain for player ${playerNumber}`, { targetGain, duration });
    
    // Ensure context is resumed before setting gain
    await ensureAudioContextResumed();
    
    const gainNode = await createMobileGainNode(playerNumber);
    // Clamp target gain to [0, 1]
    const clampedTarget = Math.max(0, Math.min(1, targetGain));
    const startGain = gainNode.gain.value;
    const startTime = audioContext?.currentTime || 0;
    
    // Cancel any scheduled automation to avoid zippering when rapidly switching
    try {
      gainNode.gain.cancelScheduledValues(startTime);
    } catch {}
    
    if (duration <= 0) {
      // Immediate set for zero-duration updates (initialization paths)
      gainNode.gain.setValueAtTime(clampedTarget, startTime);
    } else {
      gainNode.gain.setValueAtTime(startGain, startTime);
      gainNode.gain.linearRampToValueAtTime(clampedTarget, startTime + duration / 1000);
    }
    
    debugLog(`Gain fade scheduled for player ${playerNumber}`, {
      startGain,
      targetGain: clampedTarget,
      startTime
    });
  } catch (error) {
    console.error(`Failed to fade gain for player ${playerNumber}:`, error);
    throw error;
  }
};

// Set master stereo processing mode (updates state and reconnects mobile audio if needed)
export const setMasterStereoMode = async (mode: StereoMode): Promise<void> => {
  currentStereoMode = mode;
  debugLog(`Stereo mode state updated: ${mode}`);
  
  // Reconnect all mobile audio elements with new stereo mode
  for (const playerNumber of mediaElementSources.keys()) {
    try {
      await reconnectMobileAudioGraph(playerNumber);
    } catch (error) {
      console.warn(`Failed to reconnect mobile audio for player ${playerNumber} after stereo mode change:`, error);
    }
  }
};

// Toggle master dimming with smooth fade
export const toggleMasterDim = async (): Promise<boolean> => {
  console.log('[MobileAudio] Toggling master dim, current state:', isDimmed);
  // Toggle dim state
  isDimmed = !isDimmed;
  const targetGain = isDimmed ? DIM_LINEAR_GAIN : 1.0;
  console.log('[MobileAudio] New dim state:', isDimmed, 'target gain:', targetGain);
  
  // Update mobile audio context dim gain node
  const context = await initializeMobileAudioContext();
  const wasJustCreated = !masterDimGainNode;
  if (!masterDimGainNode) {
    masterDimGainNode = context.createGain();
    masterDimGainNode.gain.value = targetGain;
    masterDimGainNode.connect(context.destination);
    console.log('[MobileAudio] Created master dim gain node for mobile audio');
    debugLog('Created master dim gain node for mobile audio');
  } else {
    const currentGain = masterDimGainNode.gain.value;
    const currentTime = context.currentTime;
    console.log('[MobileAudio] Updating existing dim gain node from', currentGain, 'to', targetGain);
    masterDimGainNode.gain.cancelScheduledValues(currentTime);
    masterDimGainNode.gain.setValueAtTime(currentGain, currentTime);
    masterDimGainNode.gain.linearRampToValueAtTime(targetGain, currentTime + DIM_FADE_TIME);
  }
  
  // Always reconnect all mobile audio graphs to ensure they route through dim gain node
  // This is necessary because audio might have been connected before dim gain node existed
  console.log('[MobileAudio] Reconnecting all mobile audio graphs to ensure routing through dim gain node');
  debugLog('Reconnecting all mobile audio graphs through dim gain node');
  for (const playerNumber of mediaElementSources.keys()) {
    try {
      await reconnectMobileAudioGraph(playerNumber);
    } catch (error) {
      console.warn(`Failed to reconnect mobile audio for player ${playerNumber} after dim toggle:`, error);
    }
  }
  
  // Update all desktop (WaveSurfer) dim gain nodes
  console.log('[MobileAudio] Updating', desktopDimGainNodes.size, 'desktop dim gain nodes');
  for (const [audioContext, dimGainNode] of desktopDimGainNodes.entries()) {
    const currentGain = dimGainNode.gain.value;
    const currentTime = audioContext.currentTime;
    console.log('[MobileAudio] Updating desktop dim gain node from', currentGain, 'to', targetGain);
    dimGainNode.gain.cancelScheduledValues(currentTime);
    dimGainNode.gain.setValueAtTime(currentGain, currentTime);
    dimGainNode.gain.linearRampToValueAtTime(targetGain, currentTime + DIM_FADE_TIME);
  }
  
  console.log('[MobileAudio] Master dim', isDimmed ? 'enabled' : 'disabled');
  debugLog(`Master dim ${isDimmed ? 'enabled' : 'disabled'}`, { targetGain, wasJustCreated });
  return isDimmed;
};

// Get current dim state
export const getMasterDimState = (): boolean => {
  return isDimmed;
};

// Helper function to update existing filter node parameters (for real-time updates without zipper noise)
const updateFilterNodeParameters = (filterNodes: globalThis.BiquadFilterNode[]): void => {
  if (filterNodes.length === 0 || currentFilterType === 'off') {
    return;
  }

  const isBandpass = currentFilterType === 'bandpass';
  const filtersPerType = isBandpass ? filterNodes.length / 2 : filterNodes.length;
  const now = filterNodes[0].context.currentTime;

  for (let i = 0; i < filterNodes.length; i++) {
    const filterNode = filterNodes[i];
    
    if (isBandpass) {
      if (i < filtersPerType) {
        // Highpass filters at low frequency
        filterNode.frequency.setValueAtTime(currentFilterFrequencyLow, now);
      } else {
        // Lowpass filters at high frequency
        filterNode.frequency.setValueAtTime(currentFilterFrequencyHigh, now);
      }
    } else {
      // HP/LP filters at center frequency
      filterNode.frequency.setValueAtTime(currentFilterFrequency, now);
    }
    
    // Q is always Butterworth (0.707) - no need to update
  }
};

// Helper function to check if filter nodes can be reused (same type and slope)
const canReuseFilterNodes = (existingNodes: globalThis.BiquadFilterNode[] | undefined): boolean => {
  if (!existingNodes || existingNodes.length === 0) {
    return false;
  }

  // If filter is being turned off, can't reuse
  if (currentFilterType === 'off') {
    return false;
  }

  // Check if type changed
  if (previousFilterType !== currentFilterType) {
    return false;
  }

  // Check if slope changed (affects number of nodes)
  if (previousFilterSlope !== currentFilterSlope) {
    return false;
  }

  // Calculate expected number of nodes
  let expectedNumFilters = 1;
  if (currentFilterType === 'bandpass') {
    if (currentFilterSlope === 12) {
      expectedNumFilters = 2; // 1 HP + 1 LP
    } else if (currentFilterSlope === 24) {
      expectedNumFilters = 4; // 2 HP + 2 LP cascaded
    }
  } else {
    if (currentFilterSlope === 24) {
      expectedNumFilters = 2; // 2 cascaded filters
    }
  }

  // Check if node count matches
  if (existingNodes.length !== expectedNumFilters) {
    return false;
  }

  // Check if filter types match
  const isBandpass = currentFilterType === 'bandpass';
  const filtersPerType = isBandpass ? expectedNumFilters / 2 : expectedNumFilters;
  
  for (let i = 0; i < existingNodes.length; i++) {
    const node = existingNodes[i];
    if (isBandpass) {
      const expectedType = i < filtersPerType ? 'highpass' : 'lowpass';
      if (node.type !== expectedType) {
        return false;
      }
    } else {
      if (node.type !== currentFilterType) {
        return false;
      }
    }
  }

  return true;
};

// Helper function to create filter nodes for mobile
const createMobileFilterNodes = (context: globalThis.AudioContext): { filterNodes: globalThis.BiquadFilterNode[], inputGainNode: globalThis.GainNode | null } => {
  const filterEnabled = currentFilterType !== 'off';
  let filterNodes: globalThis.BiquadFilterNode[] = [];
  let inputGainNode: globalThis.GainNode | null = null;
  
  if (filterEnabled) {
    // Create input gain node (to prevent clipping)
    inputGainNode = context.createGain();
    inputGainNode.gain.value = 0.5; // -6dB static reduction
    
    // Calculate parameters based on filter type
    let centerFrequency = currentFilterFrequency;
    let numFilters = 1;
    let isBandpass = false;
    
      if (currentFilterType === 'bandpass') {
        isBandpass = true;
        if (currentFilterSlope === 12) {
          numFilters = 2; // 1 HP + 1 LP (each 12dB/octave)
        } else if (currentFilterSlope === 24) {
          numFilters = 4; // 2 HP + 2 LP cascaded (each pair = 24dB/octave)
        }
    } else {
      if (currentFilterSlope === 12) {
        numFilters = 1; // Single biquad = 12dB/octave
      } else if (currentFilterSlope === 24) {
        numFilters = 2; // Cascade 2 filters = 24dB/octave
      }
    }

    // Create filter nodes
    for (let i = 0; i < numFilters; i++) {
      const filterNode = context.createBiquadFilter();
      
      if (isBandpass) {
        const filtersPerType = numFilters / 2;
        if (i < filtersPerType) {
          filterNode.type = 'highpass';
          filterNode.frequency.value = currentFilterFrequencyLow;
        } else {
          filterNode.type = 'lowpass';
          filterNode.frequency.value = currentFilterFrequencyHigh;
        }
        // Use Linkwitz-Riley Q (0.5) for all filters to avoid resonance/peaking
        filterNode.Q.value = 0.5;
      } else {
        filterNode.type = currentFilterType as 'highpass' | 'lowpass';
        filterNode.frequency.value = centerFrequency;
        // Use Linkwitz-Riley Q (0.5) for minimal resonance - optimal for critical listening
        // Note: A single BiquadFilterNode is always 12dB/octave (2nd order)
        filterNode.Q.value = 0.5;
      }
      
      filterNodes.push(filterNode);
      
      // Connect filters in cascade
      if (i > 0) {
        filterNodes[i - 1].connect(filterNode);
      }
    }
  }
  
  return { filterNodes, inputGainNode };
};

// Reconnect mobile audio graph with current filter and stereo mode settings
const reconnectMobileAudioGraph = async (playerNumber: number): Promise<void> => {
  const source = mediaElementSources.get(playerNumber);
  const gainNode = gainNodes.get(playerNumber);
  
  if (!source || !gainNode) {
    debugLog(`Cannot reconnect audio graph for player ${playerNumber} - missing source or gain node`);
    return;
  }
  
  const context = await initializeMobileAudioContext();
  
  // Check if we can reuse existing filter nodes (only frequency/Q changed, not type/slope)
  const existingFilterNodes = mobileFilterNodes.get(playerNumber);
  const canReuseFilters = canReuseFilterNodes(existingFilterNodes);
  
  if (canReuseFilters && existingFilterNodes) {
    // Just update filter parameters - no need to reconnect!
    updateFilterNodeParameters(existingFilterNodes);
    debugLog(`Updated filter parameters for player ${playerNumber} (reused nodes)`);
    return;
  }
  
  // Need to recreate - disconnect existing connections
  try {
    source.disconnect();
  } catch (e) {
    // Ignore if already disconnected
  }
  
  // Clean up old filter nodes
  const oldFilterNodes = mobileFilterNodes.get(playerNumber);
  if (oldFilterNodes) {
    oldFilterNodes.forEach(node => {
      try {
        node.disconnect();
      } catch (e) {
        // Ignore disconnect errors
      }
    });
  }
  mobileFilterNodes.delete(playerNumber);
  
  const oldInputGain = mobileInputGainNodes.get(playerNumber);
  if (oldInputGain) {
    try {
      oldInputGain.disconnect();
    } catch (e) {
      // Ignore disconnect errors
    }
  }
  mobileInputGainNodes.delete(playerNumber);
  
  // Clean up old stereo processing nodes
  const oldStereoChain = mobileStereoChains.get(playerNumber);
  if (oldStereoChain) {
    try {
      oldStereoChain.splitter.disconnect();
      oldStereoChain.leftGain.disconnect();
      oldStereoChain.rightGain.disconnect();
      oldStereoChain.merger.disconnect();
    } catch (e) {
      // Ignore disconnect errors
    }
  }
  mobileStereoChains.delete(playerNumber);
  
  // Disconnect gain node from destination
  try {
    gainNode.disconnect();
  } catch (e) {
    // Ignore if already disconnected
  }
  
  // Check if we need stereo processing (mono/sides mode) or filters
  const filterEnabled = currentFilterType !== 'off';
  const needsStereoProcessing = currentStereoMode !== 'stereo' || filterEnabled;
  
  // Create new filter nodes based on current filter state
  const { filterNodes, inputGainNode } = createMobileFilterNodes(context);
  
  // Store filter nodes
  if (filterNodes.length > 0) {
    mobileFilterNodes.set(playerNumber, filterNodes);
  }
  if (inputGainNode) {
    mobileInputGainNodes.set(playerNumber, inputGainNode);
  }
  
  // Connect audio graph based on what processing is needed
  // CRITICAL: gainNode (level matching) must come BEFORE filters to prevent clipping
  // Chain: source → gainNode → [input gain] → [filter chain] → [stereo processing] → destination
  if (needsStereoProcessing) {
    // Need stereo processing: source → gainNode → [input gain] → [filter chain] → splitter → [stereo processing] → merger → destination
    const splitter = context.createChannelSplitter(2);
    const leftGain = context.createGain();
    const rightGain = context.createGain();
    const merger = context.createChannelMerger(2);
    
    // Configure stereo processing based on mode
    if (currentStereoMode === 'stereo') {
      // Filter is enabled but mode is stereo - pass through with unity gain
      leftGain.gain.value = 1.0;
      rightGain.gain.value = 1.0;
      
      splitter.connect(leftGain, 0);
      splitter.connect(rightGain, 1);
      leftGain.connect(merger, 0, 0);
      rightGain.connect(merger, 0, 1);
    } else if (currentStereoMode === 'mono') {
      // Mono: sum L+R to both channels
      leftGain.gain.value = 0.5;
      rightGain.gain.value = 0.5;
      
      splitter.connect(leftGain, 0);
      splitter.connect(rightGain, 1);
      leftGain.connect(merger, 0, 0);
      rightGain.connect(merger, 0, 0);
      leftGain.connect(merger, 0, 1);
      rightGain.connect(merger, 0, 1);
    } else if (currentStereoMode === 'side') {
      // Side-only: S = (L-R)/2
      leftGain.gain.value = 0.5;
      rightGain.gain.value = -0.5; // Negative for subtraction
      
      splitter.connect(leftGain, 0);
      splitter.connect(rightGain, 1);
      leftGain.connect(merger, 0, 0);
      rightGain.connect(merger, 0, 0);
      leftGain.connect(merger, 0, 1);
      rightGain.connect(merger, 0, 1);
    }
    
    // Store stereo processing chain
    mobileStereoChains.set(playerNumber, { splitter, leftGain, rightGain, merger });
    
    // Connect: source → gainNode → [input gain] → [filter chain] → splitter → processing → merger → destination
    source.connect(gainNode);
    if (inputGainNode && filterNodes.length > 0) {
      gainNode.connect(inputGainNode);
      inputGainNode.connect(filterNodes[0]);
      filterNodes[filterNodes.length - 1].connect(splitter);
    } else if (inputGainNode) {
      gainNode.connect(inputGainNode);
      inputGainNode.connect(splitter);
    } else if (filterNodes.length > 0) {
      gainNode.connect(filterNodes[0]);
      filterNodes[filterNodes.length - 1].connect(splitter);
    } else {
      gainNode.connect(splitter);
    }
    
    merger.connect(context.destination);
  } else {
    // No stereo processing needed: source → gainNode → [input gain] → [filter chain] → destination
    source.connect(gainNode);
    if (inputGainNode && filterNodes.length > 0) {
      gainNode.connect(inputGainNode);
      inputGainNode.connect(filterNodes[0]);
      filterNodes[filterNodes.length - 1].connect(context.destination);
    } else if (inputGainNode) {
      gainNode.connect(inputGainNode);
      inputGainNode.connect(context.destination);
    } else if (filterNodes.length > 0) {
      gainNode.connect(filterNodes[0]);
      filterNodes[filterNodes.length - 1].connect(context.destination);
    } else {
      gainNode.connect(context.destination);
    }
  }
  
  debugLog(`Reconnected audio graph for player ${playerNumber}`, {
    hasFilters: filterNodes.length > 0,
    hasInputGain: !!inputGainNode,
    stereoMode: currentStereoMode,
    needsStereoProcessing
  });
};

// Set master filter (updates state and reconnects mobile audio if needed)
export const setMasterFilter = async (
  type: FilterType,
  frequency: number = 1000,
  q: number = 0.707,
  slope: FilterSlope = 12,
  frequencyLow: number = 500,
  frequencyHigh: number = 2000,
  inputGain: number = 1.0
): Promise<void> => {
  // Update filter state (used by per-context processing)
  // Track previous state before updating
  previousFilterType = currentFilterType;
  previousFilterSlope = currentFilterSlope;
  
  currentFilterType = type;
  currentFilterFrequency = frequency;
  currentFilterFrequencyLow = frequencyLow;
  currentFilterFrequencyHigh = frequencyHigh;
  currentFilterQ = q;
  currentFilterSlope = slope;
  currentFilterInputGain = inputGain;
  
  debugLog('Filter state updated', { type, frequency, q, slope, frequencyLow, frequencyHigh, inputGain });
  
  // Reconnect all mobile audio elements with new filter settings
  // This will now reuse nodes if only frequency changed
  for (const playerNumber of mediaElementSources.keys()) {
    try {
      await reconnectMobileAudioGraph(playerNumber);
    } catch (error) {
      console.warn(`Failed to reconnect mobile audio for player ${playerNumber} after filter change:`, error);
    }
  }
};

// Per-context stereo processing for WaveSurfer instances
const wavesurferProcessingChains = new Map<number, {
  splitter: ChannelSplitterNode;
  leftGain: GainNode;
  rightGain: GainNode;
  merger: ChannelMergerNode;
  masterBus: GainNode;
  source?: AudioNode; // Store source if we created it
  audioContext?: AudioContext; // Store context for disconnection
  mode: StereoMode; // Current mode
  filter?: BiquadFilterNode; // Filter node in WaveSurfer's context (last node for compatibility)
  filterNodes?: BiquadFilterNode[]; // All filter nodes for parameter updates
}>();

// Update filter parameters for existing WaveSurfer filter nodes (fast path for real-time updates)
export const updateWaveSurferFilterParameters = (playerNumber: number, mode: StereoMode): boolean => {
  const existingChain = wavesurferProcessingChains.get(playerNumber);
  const existingFilterNodes = existingChain?.filterNodes;
  
  // Check if we can reuse: nodes exist, type/slope match, and mode matches
  if (existingFilterNodes && 
      canReuseFilterNodes(existingFilterNodes) && 
      existingChain?.mode === mode &&
      currentFilterType !== 'off') {
    updateFilterNodeParameters(existingFilterNodes);
    return true; // Successfully updated
  }
  
  return false; // Need full reconnect
};

// Connect WaveSurfer audio to stereo processing
export const connectWaveSurferToStereoMode = async (playerNumber: number, wavesurfer: any, mode: StereoMode): Promise<void> => {
  try {
    console.log(`[StereoMode] Attempting to connect WaveSurfer ${playerNumber} to ${mode} mode`, {
      hasBackend: !!wavesurfer?.backend,
      hasMedia: !!wavesurfer?.media,
      hasRenderer: !!wavesurfer?.renderer,
      wavesurferKeys: wavesurfer ? Object.keys(wavesurfer) : []
    });
    
    // Get the media wrapper from WaveSurfer
    const mediaWrapper = wavesurfer?.getMediaElement?.();
    if (!mediaWrapper) {
      console.warn(`[StereoMode] WaveSurfer ${playerNumber} has no media element`);
      return;
    }
    
    // Try to access audioContext and gainNode from the wrapper
    let audioContext: AudioContext | null = null;
    let wsSource: AudioNode | null = null;
    
    // Check if the wrapper has audioContext and gainNode
    if ((mediaWrapper as any)?.audioContext) {
      audioContext = (mediaWrapper as any).audioContext;
      console.log(`[StereoMode] Found audioContext in media wrapper for player ${playerNumber}`);
    }
    
    if ((mediaWrapper as any)?.gainNode) {
      wsSource = (mediaWrapper as any).gainNode;
      console.log(`[StereoMode] Found gainNode in media wrapper for player ${playerNumber}`);
    }
    
    // If we don't have both, try other locations
    if (!audioContext || !wsSource) {
      if (wavesurfer?.backend?.ac) {
        audioContext = wavesurfer.backend.ac;
        wsSource = wavesurfer.backend.source || wavesurfer.backend.bufferSource;
      } else if ((wavesurfer as any)?.ac) {
        audioContext = (wavesurfer as any).ac;
      } else if ((wavesurfer as any)?.renderer?.ac) {
        audioContext = (wavesurfer as any).renderer.ac;
      }
    }
    
    if (!audioContext || !wsSource) {
      console.warn(`[StereoMode] WaveSurfer ${playerNumber} has no accessible audio context or source node`, {
        hasAudioContext: !!audioContext,
        hasSource: !!wsSource,
        mediaWrapperKeys: mediaWrapper ? Object.keys(mediaWrapper) : []
      });
      return;
    }
    
    console.log(`[StereoMode] Found audio context and source for player ${playerNumber}`, {
      audioContextType: audioContext.constructor.name,
      sourceType: wsSource.constructor.name
    });
    
    // Check if filter is enabled
    const filterEnabled = currentFilterType !== 'off';
    
    // Check if we can reuse existing filter nodes (only frequency/Q changed, not type/slope)
    const existingChain = wavesurferProcessingChains.get(playerNumber);
    const existingFilterNodes = existingChain?.filterNodes;
    const canReuseFilters = filterEnabled && canReuseFilterNodes(existingFilterNodes) && existingChain?.mode === mode;
    
    if (canReuseFilters && existingFilterNodes && existingChain) {
      // Just update filter parameters - no need to reconnect!
      updateFilterNodeParameters(existingFilterNodes);
      console.log(`[StereoMode] Updated filter parameters for WaveSurfer ${playerNumber} (reused nodes)`);
      return;
    }
    
    // Need to recreate - disconnect existing chain if it exists
    if (existingChain) {
      try {
        wsSource.disconnect();
        if (existingChain.filterNodes) {
          existingChain.filterNodes.forEach(node => {
            try {
              node.disconnect();
            } catch (e) {
              // Ignore disconnect errors
            }
          });
        } else if (existingChain.filter) {
          existingChain.filter.disconnect();
        }
        existingChain.splitter.disconnect();
        existingChain.leftGain.disconnect();
        existingChain.rightGain.disconnect();
        existingChain.merger.disconnect();
      } catch (e) {
        // Ignore disconnect errors
      }
    }
    
    // Disconnect source from current destination
    try {
      wsSource.disconnect();
    } catch (e) {
      console.warn(`[StereoMode] Could not disconnect source (may already be disconnected):`, e);
    }
    
    if (mode === 'stereo' && !filterEnabled) {
      // Stereo pass-through without filter: source → dim gain → destination
      // Get or create dim gain node for this audio context
      let dimGainNode = desktopDimGainNodes.get(audioContext);
      if (!dimGainNode) {
        dimGainNode = audioContext.createGain();
        dimGainNode.gain.value = isDimmed ? DIM_LINEAR_GAIN : 1.0;
        dimGainNode.connect(audioContext.destination);
        desktopDimGainNodes.set(audioContext, dimGainNode);
        console.log(`[StereoMode] Created dim gain node for audio context in stereo pass-through`);
      }
      wsSource.connect(dimGainNode);
      wavesurferProcessingChains.delete(playerNumber);
      console.log(`[StereoMode] Successfully connected WaveSurfer ${playerNumber} to stereo (pass-through) through dim gain`);
      return;
    }
    
    // If filter is enabled or mode is not stereo, process in this context
    // (per-context processing - no master bus)
    
    // Need processing (stereo processing or filter routing)
    const splitter = audioContext.createChannelSplitter(2);
    const leftGain = audioContext.createGain();
    const rightGain = audioContext.createGain();
    const merger = audioContext.createChannelMerger(2);
    
    // Create input gain node if filter is enabled (to prevent clipping)
    // Static 6dB reduction (-6dB = 0.5x gain) to prevent filter-induced clipping
    let inputGainNode: GainNode | null = null;
    if (filterEnabled) {
      inputGainNode = audioContext.createGain();
      inputGainNode.gain.value = 0.5; // -6dB static reduction
    }
    
    // Create filter node(s) in WaveSurfer's context if filter is enabled
    let filterNodes: BiquadFilterNode[] = [];
    if (filterEnabled) {
      // Calculate parameters based on filter type
      let centerFrequency = currentFilterFrequency;
      let filterQ = currentFilterQ;
      let numFilters = 1;
      let useButterworthQ = false;
      let isBandpass = false;
      
      if (currentFilterType === 'bandpass') {
        // For BP, use two separate filters: HP at low frequency, LP at high frequency
        // Both filters use the same slope setting
        isBandpass = true;
        // Determine number of filter nodes per filter based on slope
        if (currentFilterSlope === 12) {
          numFilters = 2; // 1 HP + 1 LP (each 12dB/octave)
        } else if (currentFilterSlope === 24) {
          numFilters = 4; // 2 HP + 2 LP cascaded (each pair = 24dB/octave)
        }
      } else {
        // Determine number of filter nodes needed based on slope for HP/LP
        if (currentFilterSlope === 12) {
          numFilters = 1; // Single biquad = 12dB/octave
        } else if (currentFilterSlope === 24) {
          numFilters = 2; // Cascade 2 filters = 24dB/octave
        }
      }

      // Create filter nodes
      for (let i = 0; i < numFilters; i++) {
        const filterNode = audioContext.createBiquadFilter();
        
        if (isBandpass) {
          // For bandpass: first half are HP filters at low frequency, second half are LP filters at high frequency
          const filtersPerType = numFilters / 2;
          if (i < filtersPerType) {
            // Highpass filters at low frequency
            filterNode.type = 'highpass';
            filterNode.frequency.value = currentFilterFrequencyLow;
          } else {
            // Lowpass filters at high frequency
            filterNode.type = 'lowpass';
            filterNode.frequency.value = currentFilterFrequencyHigh;
          }
          // Use Linkwitz-Riley Q (0.5) for cascaded filters to avoid resonance
          // For single filters, use Butterworth (0.707)
          filterNode.Q.value = numFilters > 2 ? 0.5 : 0.707;
        } else {
          // For HP/LP: use the specified type and frequency
          // currentFilterType is guaranteed to be 'highpass' or 'lowpass' here (not 'off')
          filterNode.type = currentFilterType as 'highpass' | 'lowpass';
          filterNode.frequency.value = centerFrequency;
          
          // Use Linkwitz-Riley Q (0.5) for all filters to avoid resonance/peaking
          // Note: A single BiquadFilterNode is always 12dB/octave (2nd order)
          filterNode.Q.value = 0.5;
        }
        
        filterNodes.push(filterNode);
        
        // Connect filters in cascade
        if (i > 0) {
          filterNodes[i - 1].connect(filterNode);
        }
      }
    }
    
    if (mode === 'stereo') {
      // Filter is enabled but mode is stereo - pass through with unity gain
      leftGain.gain.value = 1.0;
      rightGain.gain.value = 1.0;
      
      splitter.connect(leftGain, 0);
      splitter.connect(rightGain, 1);
      leftGain.connect(merger, 0, 0);
      rightGain.connect(merger, 0, 1);
    } else if (mode === 'mono') {
      // Mono: sum L+R to both channels
      leftGain.gain.value = 0.5;
      rightGain.gain.value = 0.5;
      
      splitter.connect(leftGain, 0);
      splitter.connect(rightGain, 1);
      leftGain.connect(merger, 0, 0);
      rightGain.connect(merger, 0, 0);
      leftGain.connect(merger, 0, 1);
      rightGain.connect(merger, 0, 1);
    } else if (mode === 'side') {
      // Side-only: S = (L-R)/2
      leftGain.gain.value = 0.5;
      rightGain.gain.value = -0.5; // Negative for subtraction
      
      splitter.connect(leftGain, 0);
      splitter.connect(rightGain, 1);
      leftGain.connect(merger, 0, 0);
      rightGain.connect(merger, 0, 0);
      leftGain.connect(merger, 0, 1);
      rightGain.connect(merger, 0, 1);
    }
    
    // Connect: source → [input gain] → [filter chain] → splitter → processing → merger → destination
    if (filterEnabled && inputGainNode) {
      // Connect source to input gain, then to filter chain or splitter
      wsSource.connect(inputGainNode);
      if (filterNodes.length > 0) {
        inputGainNode.connect(filterNodes[0]);
        filterNodes[filterNodes.length - 1].connect(splitter);
      } else {
        inputGainNode.connect(splitter);
      }
    } else {
      // No filter, connect directly to splitter
      wsSource.connect(splitter);
    }
    
    // Get or create dim gain node for this audio context
    let dimGainNode = desktopDimGainNodes.get(audioContext);
    if (!dimGainNode) {
      dimGainNode = audioContext.createGain();
      dimGainNode.gain.value = isDimmed ? DIM_LINEAR_GAIN : 1.0;
      dimGainNode.connect(audioContext.destination);
      desktopDimGainNodes.set(audioContext, dimGainNode);
      console.log(`[StereoMode] Created dim gain node for audio context in processing chain`);
    }
    merger.connect(dimGainNode);
    
    // Store the chain (store all filter nodes for parameter updates, and last one for compatibility)
    wavesurferProcessingChains.set(playerNumber, {
      splitter,
      leftGain,
      rightGain,
      merger,
      masterBus: audioContext.createGain(), // Dummy for type compatibility (not used)
      source: wsSource,
      audioContext: audioContext,
      mode,
      filter: filterNodes.length > 0 ? filterNodes[filterNodes.length - 1] : undefined,
      filterNodes: filterNodes.length > 0 ? filterNodes : undefined
    });
    
    console.log(`[StereoMode] Successfully connected WaveSurfer ${playerNumber} to ${mode} processing`);
  } catch (error) {
    console.error(`[StereoMode] Failed to connect WaveSurfer ${playerNumber} to ${mode}:`, error);
    throw error;
  }
};

// Disconnect WaveSurfer from stereo processing
export const disconnectWaveSurferFromStereoMode = (playerNumber: number, wavesurfer: any): void => {
  try {
    console.log(`[StereoMode] Disconnecting WaveSurfer ${playerNumber} from stereo processing`);
    
    // Try to find the audio context
    let audioContext: AudioContext | null = null;
    let wsSource: AudioNode | null = null;
    
    if (wavesurfer?.backend?.ac) {
      audioContext = wavesurfer.backend.ac;
      wsSource = wavesurfer.backend.source || wavesurfer.backend.bufferSource;
    } else if ((wavesurfer as any)?.ac) {
      audioContext = (wavesurfer as any).ac;
    } else if ((wavesurfer as any)?.renderer?.ac) {
      audioContext = (wavesurfer as any).renderer.ac;
    }
    
    // Get stored chain
    const chain = wavesurferProcessingChains.get(playerNumber);
    if (chain) {
      // Use stored source and context if available, otherwise try to find them
      const sourceToDisconnect = chain.source || wsSource;
      const contextToUse = chain.audioContext || audioContext;
      
      if (sourceToDisconnect && contextToUse) {
        // Disconnect from processing
        try {
          sourceToDisconnect.disconnect();
        } catch (e) {
          console.warn(`[StereoMode] Could not disconnect source:`, e);
        }
        
        // Reconnect directly to destination (stereo) through dim gain node
        try {
          // Get or create dim gain node for this audio context
          let dimGainNode = desktopDimGainNodes.get(contextToUse);
          if (!dimGainNode) {
            dimGainNode = contextToUse.createGain();
            dimGainNode.gain.value = isDimmed ? DIM_LINEAR_GAIN : 1.0;
            dimGainNode.connect(contextToUse.destination);
            desktopDimGainNodes.set(contextToUse, dimGainNode);
            console.log(`[StereoMode] Created dim gain node for audio context when disconnecting from stereo processing`);
          }
          sourceToDisconnect.connect(dimGainNode);
          console.log(`[StereoMode] Reconnected WaveSurfer ${playerNumber} to destination (stereo) through dim gain`);
        } catch (e) {
          console.warn(`[StereoMode] Could not reconnect to destination:`, e);
        }
      }
    }
    
    // Clean up processing chain
    if (chain) {
      try {
        if (chain.filter) {
          chain.filter.disconnect();
        }
        chain.splitter.disconnect();
        chain.leftGain.disconnect();
        chain.rightGain.disconnect();
        chain.merger.disconnect();
      } catch (e) {
        // Ignore cleanup errors
        console.warn(`[StereoMode] Error during cleanup:`, e);
      }
      wavesurferProcessingChains.delete(playerNumber);
    }
    
    console.log(`[StereoMode] Successfully disconnected WaveSurfer ${playerNumber} from stereo processing`);
  } catch (error) {
    console.warn(`[StereoMode] Failed to disconnect WaveSurfer ${playerNumber} from stereo processing:`, error);
  }
};

// Clean up all mobile audio context references for a removed player
export const cleanupMobileAudioForPlayer = (playerNumber: number): void => {
  try {
    // Disconnect and remove gain node
    const gainNode = gainNodes.get(playerNumber);
    if (gainNode) {
      try {
        gainNode.disconnect();
      } catch (e) {
        // Ignore if already disconnected
      }
      gainNodes.delete(playerNumber);
    }

    // Disconnect and remove media element source
    const source = mediaElementSources.get(playerNumber);
    if (source) {
      try {
        source.disconnect();
      } catch (e) {
        // Ignore if already disconnected
      }
      mediaElementSources.delete(playerNumber);
    }

    // Clean up filter nodes
    const filterNodes = mobileFilterNodes.get(playerNumber);
    if (filterNodes) {
      filterNodes.forEach(node => {
        try {
          node.disconnect();
        } catch (e) {
          // Ignore disconnect errors
        }
      });
      mobileFilterNodes.delete(playerNumber);
    }

    // Clean up input gain node
    const inputGain = mobileInputGainNodes.get(playerNumber);
    if (inputGain) {
      try {
        inputGain.disconnect();
      } catch (e) {
        // Ignore disconnect errors
      }
      mobileInputGainNodes.delete(playerNumber);
    }

    // Clean up stereo processing chain
    const stereoChain = mobileStereoChains.get(playerNumber);
    if (stereoChain) {
      try {
        stereoChain.splitter.disconnect();
        stereoChain.leftGain.disconnect();
        stereoChain.rightGain.disconnect();
        stereoChain.merger.disconnect();
      } catch (e) {
        // Ignore disconnect errors
      }
      mobileStereoChains.delete(playerNumber);
    }

    // Clean up file ID mapping
    for (const [fileId, mappedPlayerNumber] of fileIdToPlayerNumber.entries()) {
      if (mappedPlayerNumber === playerNumber) {
        fileIdToPlayerNumber.delete(fileId);
        debugLog(`Cleaned up file ID mapping: ${fileId} -> player ${playerNumber}`);
      }
    }

    console.log(`[MobileAudio] Cleaned up all references for player ${playerNumber}`);
  } catch (error) {
    console.warn(`[MobileAudio] Error cleaning up player ${playerNumber}:`, error);
  }
};

// Remap mobile audio connections based on file ID mappings
// This is used when players are removed and remaining players are renumbered
export const remapMobileAudioConnections = (fileIdToNewPlayerNumber: Map<string, number>, removedFileId?: string): void => {
  try {
    console.log(`[MobileAudio] Remapping connections based on file IDs`, {
      mappings: Array.from(fileIdToNewPlayerNumber.entries()),
      removedFileId
    });

    // Create temporary maps to store connections by file ID
    const fileIdToSource = new Map<string, globalThis.MediaElementAudioSourceNode>();
    const fileIdToGainNode = new Map<string, globalThis.GainNode>();
    const fileIdToFilterNodes = new Map<string, globalThis.BiquadFilterNode[]>();
    const fileIdToInputGain = new Map<string, globalThis.GainNode>();
    const fileIdToStereoChain = new Map<string, {
      splitter: ChannelSplitterNode;
      leftGain: GainNode;
      rightGain: GainNode;
      merger: ChannelMergerNode;
    }>();

    // First, collect all existing connections by their current file ID mapping
    for (const [fileId, oldPlayerNumber] of fileIdToPlayerNumber.entries()) {
      if (fileId === removedFileId) {
        // Skip the removed file
        continue;
      }

      const source = mediaElementSources.get(oldPlayerNumber);
      const gainNode = gainNodes.get(oldPlayerNumber);
      const filterNodes = mobileFilterNodes.get(oldPlayerNumber);
      const inputGain = mobileInputGainNodes.get(oldPlayerNumber);
      const stereoChain = mobileStereoChains.get(oldPlayerNumber);

      if (source) fileIdToSource.set(fileId, source);
      if (gainNode) fileIdToGainNode.set(fileId, gainNode);
      if (filterNodes) fileIdToFilterNodes.set(fileId, filterNodes);
      if (inputGain) fileIdToInputGain.set(fileId, inputGain);
      if (stereoChain) fileIdToStereoChain.set(fileId, stereoChain);
    }

    // Clear all existing mappings
    mediaElementSources.clear();
    gainNodes.clear();
    mobileFilterNodes.clear();
    mobileInputGainNodes.clear();
    mobileStereoChains.clear();
    fileIdToPlayerNumber.clear();

    // Remap connections to new player numbers based on file IDs
    for (const [fileId, newPlayerNumber] of fileIdToNewPlayerNumber.entries()) {
      const source = fileIdToSource.get(fileId);
      const gainNode = fileIdToGainNode.get(fileId);
      const filterNodes = fileIdToFilterNodes.get(fileId);
      const inputGain = fileIdToInputGain.get(fileId);
      const stereoChain = fileIdToStereoChain.get(fileId);

      if (source) {
        mediaElementSources.set(newPlayerNumber, source);
        console.log(`[MobileAudio] Remapped source for file ${fileId} from old player to player ${newPlayerNumber}`);
      }
      if (gainNode) {
        gainNodes.set(newPlayerNumber, gainNode);
        console.log(`[MobileAudio] Remapped gain node for file ${fileId} to player ${newPlayerNumber}`);
      }
      if (filterNodes) {
        mobileFilterNodes.set(newPlayerNumber, filterNodes);
        console.log(`[MobileAudio] Remapped filter nodes for file ${fileId} to player ${newPlayerNumber}`);
      }
      if (inputGain) {
        mobileInputGainNodes.set(newPlayerNumber, inputGain);
        console.log(`[MobileAudio] Remapped input gain for file ${fileId} to player ${newPlayerNumber}`);
      }
      if (stereoChain) {
        mobileStereoChains.set(newPlayerNumber, stereoChain);
        console.log(`[MobileAudio] Remapped stereo chain for file ${fileId} to player ${newPlayerNumber}`);
      }

      // Update file ID to player number mapping
      fileIdToPlayerNumber.set(fileId, newPlayerNumber);
    }

    console.log(`[MobileAudio] Remapping complete`, {
      remappedPlayers: Array.from(mediaElementSources.keys()),
      fileIdMappings: Array.from(fileIdToPlayerNumber.entries())
    });
  } catch (error) {
    console.error(`[MobileAudio] Error remapping connections:`, error);
    throw error;
  }
}; 